import React, { useCallback, useEffect, useRef, useState } from "react";

import "../css/ProjectKits.css";

import projectKitImage from "../images/mindspark-project-kit.png";

import {
  fetchProjectKitCatalog,
  fetchProjectKitSchools,
  placeProjectKitOrder,
  previewProjectKitCoupon,
} from "../common/ProjectKitsAPI";

const gradeOptions = [
  "Pre-Primary",
  "Grade 1",
  "Grade 2",
  "Grade 3",
  "Grade 4",
  "Grade 5",
  "Grade 6",
  "Grade 7",
  "Grade 8",
  "Grade 9",
  "Grade 10",
];

const ordererOptions = ["Parent / Guardian", "School", "Student"];

const deliveryOptions = ["Home Delivery", "Delivery to School"];

const paymentOptions = [
  "Cash on Delivery",
  "Payment through School",
  "Confirm payment options with MindSpark",
];

const ordererValues = {
  "Parent / Guardian": "parent_guardian",
  School: "school",
  Student: "student",
};

const deliveryValues = {
  "Delivery to School": "school",
  "Home Delivery": "home",
};

const paymentValues = {
  "Cash on Delivery": "cod",
  "Payment through School": "school",
  "Confirm payment options with MindSpark": "contact_mindspark",
};

const formatMoney = (value, currency = "INR") => {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      maximumFractionDigits: 0,
    }).format(Number(value || 0));
  } catch (_error) {
    return `₹${Number(value || 0).toLocaleString("en-IN")}`;
  }
};

const getKitId = (kit) => kit?.offering_item_id || "";

const getKitTitle = (kit) => kit?.kit_title || "Project Kit";

const getKitStock = (kit) =>
  Math.max(
    0,
    Math.floor(
      Number(
        kit?.quantity_assembled_in_stock ||
          0,
      ),
    ),
  );

/*
 * Quantity is limited ONLY by current stock.
 *
 * Example:
 * stock = 25
 * customer can order 1 - 25
 *
 * No fixed max 10.
 */
const getKitMaxQuantity = (kit) =>
  getKitStock(kit);

const getKitDescription = (kit) => {
  const grade =
    kit?.target_grade_or_level ||
    "the selected grade";

  const projects = Math.max(
    0,
    Number(
      kit?.target_projects_count ||
        0,
    ),
  );

  if (projects > 0) {
    return `Includes ${projects} hands-on project${
      projects === 1 ? "" : "s"
    } for ${grade}, made for continued practice at home.`;
  }

  return `A hands-on Project Kit for ${grade}, made for continued practice at home.`;
};

const initialOrderForm = {
  orderedBy: "Parent / Guardian",
  studentName: "",
  grade: "",
  quantity: 1,
  deliveryPreference:
    "Delivery to School",
  mobile: "",
  email: "",
  promoCode: "",
  address: "",
  city: "",
  state: "Maharashtra",
  pinCode: "",
  paymentMethod:
    "Cash on Delivery",
};

export default function ProjectKits() {
  const catalogSectionRef =
    useRef(null);

  const orderSectionRef =
    useRef(null);

  const catalogRequestIdRef =
    useRef(0);

  const checkoutIdempotencyKeyRef =
    useRef(null);

  const checkoutPayloadSignatureRef =
    useRef("");

  const [
    schools,
    setSchools,
  ] = useState([]);

  const [
    selectedSchoolId,
    setSelectedSchoolId,
  ] = useState("");

  const [
    schoolsLoading,
    setSchoolsLoading,
  ] = useState(true);

  const [
    schoolsError,
    setSchoolsError,
  ] = useState("");

  const [
    kits,
    setKits,
  ] = useState([]);

  const [
    selectedKitId,
    setSelectedKitId,
  ] = useState("");

  const [
    catalogStatus,
    setCatalogStatus,
  ] = useState("idle");

  const [
    catalogError,
    setCatalogError,
  ] = useState("");

  const [
    catalogReloadKey,
    setCatalogReloadKey,
  ] = useState(0);

  const [
    orderForm,
    setOrderForm,
  ] = useState(
    initialOrderForm,
  );

  const [
    isSubmitting,
    setIsSubmitting,
  ] = useState(false);

  const [
    orderError,
    setOrderError,
  ] = useState("");

  const [
    orderSuccess,
    setOrderSuccess,
  ] = useState(null);

  /*
   * ----------------------------------------------------------
   * STUDENT COUPON STATE
   * ----------------------------------------------------------
   */

  const [
    couponPreview,
    setCouponPreview,
  ] = useState(null);

  const [
    couponStatus,
    setCouponStatus,
  ] = useState("idle");

  const [
    couponMessage,
    setCouponMessage,
  ] = useState("");

  const [
    couponAppliedSignature,
    setCouponAppliedSignature,
  ] = useState("");

  const [
    isApplyingCoupon,
    setIsApplyingCoupon,
  ] = useState(false);

  /*
   * ----------------------------------------------------------
   * LOAD SCHOOLS
   * ----------------------------------------------------------
   */

  const loadSchools =
    useCallback(
      async () => {
        setSchoolsLoading(true);
        setSchoolsError("");

        try {
          const data =
            await fetchProjectKitSchools();

          const nextSchools =
            Array.isArray(
              data?.schools,
            )
              ? data.schools
              : [];

          setSchools(
            nextSchools,
          );

          setSelectedSchoolId(
            (currentId) =>
              nextSchools.some(
                (school) =>
                  school.school_id ===
                  currentId,
              )
                ? currentId
                : "",
          );
        } catch (error) {
          setSchools([]);

          setSelectedSchoolId(
            "",
          );

          setSchoolsError(
            error?.message ||
              "Unable to load schools. Please try again.",
          );
        } finally {
          setSchoolsLoading(
            false,
          );
        }
      },
      [],
    );

  useEffect(() => {
    loadSchools();
  }, [loadSchools]);

  /*
   * ----------------------------------------------------------
   * LOAD PROJECT KIT CATALOG
   * ----------------------------------------------------------
   */

  useEffect(() => {
    setKits([]);

    setSelectedKitId("");

    setCatalogError("");

    if (
      !selectedSchoolId ||
      !orderForm.grade
    ) {
      setCatalogStatus(
        "idle",
      );

      return undefined;
    }

    const controller =
      new AbortController();

    const requestId =
      ++catalogRequestIdRef.current;

    setCatalogStatus(
      "loading",
    );

    const loadCatalog =
      async () => {
        try {
          const data =
            await fetchProjectKitCatalog(
              {
                schoolId:
                  selectedSchoolId,

                grade:
                  orderForm.grade,

                signal:
                  controller.signal,
              },
            );

          if (
            requestId !==
            catalogRequestIdRef.current
          ) {
            return;
          }

          const nextKits =
            Array.isArray(
              data?.kits,
            )
              ? data.kits
              : [];

          setKits(
            nextKits,
          );

          setCatalogStatus(
            nextKits.length
              ? "success"
              : "empty",
          );
        } catch (error) {
          if (
            error?.name ===
            "AbortError"
          ) {
            return;
          }

          if (
            requestId !==
            catalogRequestIdRef.current
          ) {
            return;
          }

          setKits([]);

          setCatalogError(
            error?.message ||
              "Unable to load Project Kits.",
          );

          setCatalogStatus(
            "error",
          );
        }
      };

    loadCatalog();

    return () =>
      controller.abort();
  }, [
    selectedSchoolId,
    orderForm.grade,
    catalogReloadKey,
  ]);

  /*
   * ----------------------------------------------------------
   * SELECTED DATA
   * ----------------------------------------------------------
   */

  const selectedKit =
    kits.find(
      (kit) =>
        getKitId(kit) ===
        selectedKitId,
    ) || null;

  const selectedSchool =
    schools.find(
      (school) =>
        school.school_id ===
        selectedSchoolId,
    ) || null;

  const quantity = Math.max(
    1,
    Number(
      orderForm.quantity ||
        1,
    ),
  );

  const selectedKitStock =
    getKitStock(
      selectedKit,
    );

  const maxQuantity =
    getKitMaxQuantity(
      selectedKit,
    );

  const subtotal =
    Number(
      selectedKit?.selling_price ||
        0,
    ) * quantity;

  /*
   * ----------------------------------------------------------
   * COUPON TOTALS
   * ----------------------------------------------------------
   */

  const couponDiscount =
    couponPreview
      ? Number(
          couponPreview.discountAmount ??
            0,
        )
      : 0;

  const finalTotal =
    couponPreview
      ? Number(
          couponPreview.finalAmount ??
            subtotal,
        )
      : subtotal;

  /*
   * This signature represents everything
   * relevant to website coupon eligibility.
   *
   * Name/mobile are NOT part of the coupon
   * identity because website checkout is a
   * guest checkout.
   */
  const couponContextSignature =
    JSON.stringify({
      offeringItemId:
        selectedKitId,

      schoolId:
        selectedSchoolId,

      grade:
        orderForm.grade,

      quantity,

      promoCode:
        orderForm.promoCode
          .trim()
          .toUpperCase(),
    });

  /*
   * If school, grade, kit, quantity or
   * coupon code changes after Apply,
   * invalidate the previous quote.
   */
  useEffect(() => {
    if (
      couponPreview &&
      couponAppliedSignature &&
      couponAppliedSignature !==
        couponContextSignature
    ) {
      setCouponPreview(
        null,
      );

      setCouponAppliedSignature(
        "",
      );

      setCouponStatus(
        "idle",
      );

      setCouponMessage(
        "",
      );
    }
  }, [
    couponContextSignature,
    couponAppliedSignature,
    couponPreview,
  ]);

  /*
   * ----------------------------------------------------------
   * GENERIC FIELD UPDATE
   * ----------------------------------------------------------
   */

  const updateField = (
    field,
    value,
  ) => {
    setOrderForm(
      (current) => ({
        ...current,
        [field]: value,
      }),
    );

    setOrderError("");
  };

  /*
   * ----------------------------------------------------------
   * CLEAR COUPON
   * ----------------------------------------------------------
   */

  const clearCoupon = ({
    clearCode = false,
  } = {}) => {
    setCouponPreview(
      null,
    );

    setCouponAppliedSignature(
      "",
    );

    setCouponStatus(
      "idle",
    );

    setCouponMessage(
      "",
    );

    if (clearCode) {
      setOrderForm(
        (current) => ({
          ...current,
          promoCode: "",
        }),
      );
    }
  };

  /*
   * ----------------------------------------------------------
   * CATALOG HELPERS
   * ----------------------------------------------------------
   */

  const clearCatalogImmediately =
    () => {
      catalogRequestIdRef.current +=
        1;

      setKits([]);

      setSelectedKitId("");

      setCatalogError("");

      setCatalogStatus(
        "idle",
      );

      setOrderError("");

      clearCoupon();
    };

  const handleSchoolChange = (
    schoolId,
  ) => {
    clearCatalogImmediately();

    setSelectedSchoolId(
      schoolId,
    );
  };

  const handleGradeChange = (
    grade,
  ) => {
    clearCatalogImmediately();

    updateField(
      "grade",
      grade,
    );
  };

  const selectKit = (
    kit,
    shouldScroll = false,
  ) => {
    if (
      !kit ||
      getKitStock(kit) <= 0
    ) {
      return;
    }

    setSelectedKitId(
      getKitId(kit),
    );

    setOrderForm(
      (current) => ({
        ...current,

        quantity:
          Math.min(
            Math.max(
              1,
              Number(
                current.quantity ||
                  1,
              ),
            ),
            getKitMaxQuantity(
              kit,
            ),
          ),
      }),
    );

    setOrderError("");

    clearCoupon();

    if (shouldScroll) {
      scrollToOrder();
    }
  };

  const scrollToCatalog =
    () => {
      catalogSectionRef.current?.scrollIntoView(
        {
          behavior:
            "smooth",

          block:
            "start",
        },
      );
    };

  const scrollToOrder =
    () => {
      orderSectionRef.current?.scrollIntoView(
        {
          behavior:
            "smooth",

          block:
            "start",
        },
      );
    };

  /*
   * ----------------------------------------------------------
   * COUPON VALIDATION
   *
   * No Enrollment ID.
   * No registered phone check.
   *
   * Backend remains authoritative.
   * ----------------------------------------------------------
   */

  const validateCouponInput =
    () => {
      if (
        !selectedSchoolId ||
        !selectedSchool
      ) {
        return "Select the student's school first.";
      }

      if (
        !orderForm.grade
      ) {
        return "Select the student's grade first.";
      }

      if (
        !selectedKit ||
        !selectedKitId
      ) {
        return "Select a Project Kit first.";
      }

      if (
        !orderForm.promoCode.trim()
      ) {
        return "Enter a coupon code.";
      }

      if (
        !Number.isInteger(
          quantity,
        ) ||
        quantity < 1
      ) {
        return "Quantity must be a whole number of at least 1.";
      }

      if (
        quantity >
        selectedKitStock
      ) {
        return `Only ${selectedKitStock} kit${
          selectedKitStock ===
          1
            ? " is"
            : "s are"
        } currently in stock.`;
      }

      return "";
    };

  /*
   * ----------------------------------------------------------
   * APPLY COUPON
   * ----------------------------------------------------------
   */

  const applyCoupon =
    async () => {
      if (
        isApplyingCoupon ||
        isSubmitting
      ) {
        return;
      }

      const validationError =
        validateCouponInput();

      if (
        validationError
      ) {
        setCouponPreview(
          null,
        );

        setCouponStatus(
          "error",
        );

        setCouponMessage(
          validationError,
        );

        return;
      }

      setIsApplyingCoupon(
        true,
      );

      setCouponPreview(
        null,
      );

      setCouponAppliedSignature(
        "",
      );

      setCouponStatus(
        "loading",
      );

      setCouponMessage(
        "",
      );

      setOrderError("");

      try {
        const data =
          await previewProjectKitCoupon(
            {
              offeringItemId:
                selectedKitId,

              schoolId:
                selectedSchoolId,

              grade:
                orderForm.grade,

              quantity,

              promoCode:
                orderForm.promoCode.trim(),
            },
          );

        if (
          !data?.coupon?.success
        ) {
          throw new Error(
            "The coupon could not be verified.",
          );
        }

        const canonicalCode =
          String(
            data.coupon
              .couponCode ||
              orderForm.promoCode,
          )
            .trim()
            .toUpperCase();

        setOrderForm(
          (current) => ({
            ...current,

            promoCode:
              canonicalCode,
          }),
        );

        const nextSignature =
          JSON.stringify({
            offeringItemId:
              selectedKitId,

            schoolId:
              selectedSchoolId,

            grade:
              orderForm.grade,

            quantity,

            promoCode:
              canonicalCode,
          });

        setCouponPreview(
          data.coupon,
        );

        setCouponAppliedSignature(
          nextSignature,
        );

        setCouponStatus(
          "applied",
        );

        setCouponMessage(
          `${canonicalCode} applied successfully.`,
        );
      } catch (error) {
        setCouponPreview(
          null,
        );

        setCouponAppliedSignature(
          "",
        );

        setCouponStatus(
          "error",
        );

        setCouponMessage(
          error?.message ||
            "We couldn't apply this coupon.",
        );
      } finally {
        setIsApplyingCoupon(
          false,
        );
      }
    };

  const removeCoupon =
    () => {
      clearCoupon({
        clearCode:
          true,
      });

      setOrderError("");
    };

  /*
   * ----------------------------------------------------------
   * ORDER VALIDATION
   * ----------------------------------------------------------
   */

  const validateOrder =
    () => {
      if (
        !selectedSchoolId ||
        !selectedSchool
      ) {
        return "Please select a valid school.";
      }

      if (
        !orderForm.grade
      ) {
        return "Please select the student's grade.";
      }

      if (
        !selectedKit ||
        !selectedKitId
      ) {
        return "Please select an available Project Kit.";
      }

      if (
        selectedKitStock <= 0
      ) {
        return "The selected Project Kit is out of stock.";
      }

      if (
        !ordererValues[
          orderForm.orderedBy
        ]
      ) {
        return "Please select who is placing the order.";
      }

      if (
        !orderForm.studentName.trim()
      ) {
        return "Please enter the student's full name.";
      }

      const compactMobile =
        orderForm.mobile
          .trim()
          .replace(
            /[\s()-]/g,
            "",
          );

      if (
        !/^\+?\d{8,15}$/.test(
          compactMobile,
        )
      ) {
        return "Please enter a valid parent or guardian mobile number.";
      }

      const email =
        orderForm.email.trim();

      if (
        email &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          email,
        )
      ) {
        return "Please enter a valid email address or leave it blank.";
      }

      const requestedQuantity =
        Number(
          orderForm.quantity,
        );

      if (
        !Number.isInteger(
          requestedQuantity,
        ) ||
        requestedQuantity <
          1
      ) {
        return "Quantity must be a whole number of at least 1.";
      }

      /*
       * No fixed quantity limit.
       * Stock is the only maximum.
       */
      if (
        requestedQuantity >
        selectedKitStock
      ) {
        return `Only ${selectedKitStock} kit${
          selectedKitStock ===
          1
            ? " is"
            : "s are"
        } currently in stock.`;
      }

      if (
        !deliveryValues[
          orderForm
            .deliveryPreference
        ]
      ) {
        return "Please select a delivery preference.";
      }

      if (
        !paymentValues[
          orderForm.paymentMethod
        ]
      ) {
        return "Please select a payment method.";
      }

      /*
       * If a code exists, customer must
       * click Apply successfully before
       * placing the order.
       */
      if (
        orderForm.promoCode.trim()
      ) {
        if (
          !couponPreview ||
          couponStatus !==
            "applied"
        ) {
          return "Please click Apply Coupon and verify the coupon before placing the order.";
        }

        if (
          couponAppliedSignature !==
          couponContextSignature
        ) {
          return "The coupon details changed. Please apply the coupon again.";
        }
      }

      if (
        orderForm
          .deliveryPreference ===
        "Home Delivery"
      ) {
        if (
          !orderForm.address.trim()
        ) {
          return "Please enter the full delivery address.";
        }

        if (
          !orderForm.city.trim()
        ) {
          return "Please enter the delivery city.";
        }

        if (
          !orderForm.state.trim()
        ) {
          return "Please enter the delivery state.";
        }

        if (
          !/^[1-9][0-9]{5}$/.test(
            orderForm.pinCode.trim(),
          )
        ) {
          return "Please enter a valid 6-digit PIN code that does not begin with zero.";
        }
      }

      return "";
    };

  /*
   * ----------------------------------------------------------
   * PLACE ORDER
   * ----------------------------------------------------------
   */

  const handleSubmit =
    async (event) => {
      event.preventDefault();

      if (
        isSubmitting
      ) {
        return;
      }

      const validationError =
        validateOrder();

      if (
        validationError
      ) {
        setOrderError(
          validationError,
        );

        return;
      }

      const homeDelivery =
        orderForm
          .deliveryPreference ===
        "Home Delivery";

      const hasAppliedCoupon =
        Boolean(
          couponPreview &&
            orderForm.promoCode.trim(),
        );

      const orderPayload = {
        offeringItemId:
          selectedKitId,

        schoolId:
          selectedSchoolId,

        grade:
          orderForm.grade,

        orderedBy:
          ordererValues[
            orderForm
              .orderedBy
          ],

        studentName:
          orderForm.studentName.trim(),

        parentMobile:
          orderForm.mobile.trim(),

        email:
          orderForm.email.trim(),

        quantity:
          Number(
            orderForm.quantity,
          ),

        deliveryPreference:
          deliveryValues[
            orderForm
              .deliveryPreference
          ],

        paymentMethod:
          paymentValues[
            orderForm
              .paymentMethod
          ],

        address:
          homeDelivery
            ? orderForm.address.trim()
            : "",

        city:
          homeDelivery
            ? orderForm.city.trim()
            : "",

        state:
          homeDelivery
            ? orderForm.state.trim()
            : "",

        pinCode:
          homeDelivery
            ? orderForm.pinCode.trim()
            : "",

        promoCode:
          hasAppliedCoupon
            ? orderForm.promoCode.trim()
            : "",
      };

      /*
       * Retry same payload:
       * reuse same idempotency key.
       *
       * New/different order:
       * generate a new key.
       */
      const payloadSignature =
        JSON.stringify(
          orderPayload,
        );

      if (
        !checkoutIdempotencyKeyRef.current ||
        checkoutPayloadSignatureRef.current !==
          payloadSignature
      ) {
        checkoutIdempotencyKeyRef.current =
          crypto.randomUUID();

        checkoutPayloadSignatureRef.current =
          payloadSignature;
      }

      setIsSubmitting(true);

      setOrderError("");

      try {
        const data =
          await placeProjectKitOrder(
            {
              ...orderPayload,

              checkoutIdempotencyKey:
                checkoutIdempotencyKeyRef.current,
            },
          );

        if (
          !data?.order
        ) {
          throw new Error(
            "The order was received, but its confirmation was incomplete. Please contact MindSpark before retrying.",
          );
        }

        setOrderSuccess(
          data.order,
        );

        /*
         * Server has already deducted stock.
         * Refresh catalogue from backend.
         */
        setCatalogReloadKey(
          (value) =>
            value + 1,
        );

        /*
         * Successful checkout must
         * never reuse its key.
         */
        checkoutIdempotencyKeyRef.current =
          null;

        checkoutPayloadSignatureRef.current =
          "";
      } catch (error) {
        setOrderError(
          error?.message ||
            "We couldn't place the order. Please try again.",
        );
      } finally {
        setIsSubmitting(
          false,
        );
      }
    };

  /*
   * ----------------------------------------------------------
   * START ANOTHER ORDER
   * ----------------------------------------------------------
   */

  const startAnotherOrder =
    () => {
      checkoutIdempotencyKeyRef.current =
        null;

      checkoutPayloadSignatureRef.current =
        "";

      setOrderSuccess(
        null,
      );

      setOrderError("");

      setCouponPreview(
        null,
      );

      setCouponAppliedSignature(
        "",
      );

      setCouponStatus(
        "idle",
      );

      setCouponMessage(
        "",
      );

      setOrderForm(
        (current) => ({
          ...initialOrderForm,

          grade:
            current.grade,
        }),
      );

      setSelectedKitId(
        "",
      );

      /*
       * Refresh stock before the
       * next order starts.
       */
      setCatalogReloadKey(
        (value) =>
          value + 1,
      );

      window.requestAnimationFrame(
        () => {
          catalogSectionRef.current?.scrollIntoView(
            {
              behavior:
                "smooth",

              block:
                "start",
            },
          );
        },
      );
    };

  return (
    <main className="pk-page">
      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="pk-hero">
        <div
          className="pk-grid-overlay"
          aria-hidden="true"
        />

        <div
          className="pk-hero-orb pk-hero-orb-one"
          aria-hidden="true"
        />

        <div
          className="pk-hero-orb pk-hero-orb-two"
          aria-hidden="true"
        />

        <div className="pk-shell pk-hero-layout">
          <div className="pk-hero-copy">
            <div className="pk-eyebrow">
              <span className="pk-eyebrow-dot" />

              LEARNING BEYOND THE CLASSROOM
            </div>

            <h1>
              Find your
              <br />
              <span>
                Project Kit.
              </span>
            </h1>

            <p className="pk-hero-lead">
              Choose your school and grade to see the MindSpark Project Kit
              available for you, then place your order online.
            </p>

            <div className="pk-hero-actions">
              <button
                type="button"
                className="pk-primary-btn"
                onClick={
                  scrollToCatalog
                }
              >
                Find My Kit

                <span aria-hidden="true">
                  →
                </span>
              </button>

              <a
                href="#how-it-works"
                className="pk-secondary-btn"
              >
                How it works
              </a>
            </div>

            <div
              className="pk-topic-row"
              aria-label="Learning areas"
            >
              {[
                "Robotics",
                "Coding",
                "AI",
                "Electronics",
              ].map(
                (item) => (
                  <span
                    key={item}
                  >
                    {item}
                  </span>
                ),
              )}
            </div>
          </div>

          <div className="pk-hero-visual">
            <div
              className="pk-product-glow"
              aria-hidden="true"
            />

            <div className="pk-image-card">
              <div className="pk-image-topline">
                <span>
                  MindSpark Project Kits
                </span>

                <strong>
                  Hands-on Learning
                </strong>
              </div>

              <img
                src={
                  projectKitImage
                }
                alt="MindSpark EduTech robotics, coding, AI and electronics project kit"
                className="pk-main-image"
              />

              <div className="pk-image-floating-card">
                <span className="pk-mini-label">
                  PROJECT KIT
                </span>

                <strong>
                  Made for your grade
                </strong>

                <p>
                  Hands-on projects for continued practice at home.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          CATALOG
      ===================================================== */}

      <section
        ref={
          catalogSectionRef
        }
        className="pk-catalog-section"
      >
        <div className="pk-shell">
          <div className="pk-section-heading">
            <div>
              <span className="pk-section-kicker">
                PROJECT KITS
              </span>

              <h2>
                Find the kit for your school and grade.
              </h2>
            </div>

            <p>
              Select the student's school and grade. We will show the Project
              Kit currently available for that selection.
            </p>
          </div>

          <div className="pk-kit-finder">
            <div className="pk-finder-field">
              <label htmlFor="catalogSchool">
                School
              </label>

              <select
                id="catalogSchool"
                value={
                  selectedSchoolId
                }
                onChange={(
                  event,
                ) =>
                  handleSchoolChange(
                    event.target.value,
                  )
                }
                disabled={
                  schoolsLoading ||
                  Boolean(
                    schoolsError,
                  )
                }
              >
                <option value="">
                  {schoolsLoading
                    ? "Loading schools…"
                    : schools.length
                      ? "Select school"
                      : "No schools available"}
                </option>

                {schools.map(
                  (school) => (
                    <option
                      key={
                        school.school_id
                      }
                      value={
                        school.school_id
                      }
                    >
                      {
                        school.school_name
                      }
                    </option>
                  ),
                )}
              </select>

              {schoolsError && (
                <div
                  className="pk-field-state error"
                  role="alert"
                >
                  <span>
                    {schoolsError}
                  </span>

                  <button
                    type="button"
                    onClick={
                      loadSchools
                    }
                  >
                    Retry
                  </button>
                </div>
              )}

              {!schoolsLoading &&
                !schoolsError &&
                schools.length ===
                  0 && (
                  <div className="pk-field-state empty">
                    No schools are currently available for website ordering.
                  </div>
                )}
            </div>

            <div className="pk-finder-field">
              <label htmlFor="catalogGrade">
                Grade
              </label>

              <select
                id="catalogGrade"
                value={
                  orderForm.grade
                }
                onChange={(
                  event,
                ) =>
                  handleGradeChange(
                    event.target.value,
                  )
                }
              >
                <option value="">
                  Select grade
                </option>

                {gradeOptions.map(
                  (grade) => (
                    <option
                      key={
                        grade
                      }
                      value={
                        grade
                      }
                    >
                      {grade}
                    </option>
                  ),
                )}
              </select>
            </div>

            <div className="pk-finder-help">
              <span>
                1
              </span>

              <p>
                Select your school and grade to check the available kit.
              </p>
            </div>
          </div>

          <div className="pk-catalog-grid">
            {catalogStatus ===
              "idle" && (
              <div className="pk-catalog-hint">
                <span aria-hidden="true">
                  ↑
                </span>

                <p>
                  Choose a school and grade above to view the available kit.
                </p>
              </div>
            )}

            {catalogStatus ===
              "loading" && (
              <div
                className="pk-status-panel loading"
                role="status"
              >
                <span
                  className="pk-spinner"
                  aria-hidden="true"
                />

                <strong>
                  Checking available Project Kits…
                </strong>
              </div>
            )}

            {catalogStatus ===
              "error" && (
              <div
                className="pk-status-panel error"
                role="alert"
              >
                <strong>
                  We couldn't load the available kit.
                </strong>

                <span>
                  {catalogError}
                </span>

                <button
                  type="button"
                  onClick={() => {
                    clearCatalogImmediately();

                    setCatalogReloadKey(
                      (
                        current,
                      ) =>
                        current +
                        1,
                    );
                  }}
                >
                  Try Again
                </button>
              </div>
            )}

            {catalogStatus ===
              "empty" && (
              <div className="pk-status-panel empty">
                <strong>
                  No Project Kit is available for this selection.
                </strong>

                <span>
                  Try another grade or contact MindSpark for help.
                </span>
              </div>
            )}

            {kits.length >
              0 && (
              <div className="pk-kit-list">
                {kits.map(
                  (kit) => {
                    const kitId =
                      getKitId(
                        kit,
                      );

                    const stock =
                      getKitStock(
                        kit,
                      );

                    const active =
                      kitId ===
                      selectedKitId;

                    return (
                      <article
                        key={
                          kitId
                        }
                        className={`pk-kit-card ${
                          active
                            ? "selected"
                            : ""
                        } ${
                          stock <=
                          0
                            ? "out-of-stock"
                            : ""
                        }`}
                      >
                        <div className="pk-kit-image-wrap">
                          <img
                            src={
                              kit.offering_image_url ||
                              projectKitImage
                            }
                            alt={
                              getKitTitle(
                                kit,
                              )
                            }
                          />

                          <span
                            className={`pk-active-badge ${
                              stock <=
                              0
                                ? "unavailable"
                                : ""
                            }`}
                          >
                            {stock <=
                            0
                              ? "Out of Stock"
                              : `${stock} in stock`}
                          </span>
                        </div>

                        <div className="pk-kit-content">
                          <span className="pk-card-eyebrow">
                            MindSpark Project Kit
                          </span>

                          <h3>
                            {getKitTitle(
                              kit,
                            )}
                          </h3>

                          <p>
                            {getKitDescription(
                              kit,
                            )}
                          </p>

                          <div className="pk-kit-meta">
                            <div>
                              <span>
                                For
                              </span>

                              <strong className="pk-kit-grade">
                                {kit.target_grade_or_level ||
                                  orderForm.grade}
                              </strong>
                            </div>

                            <div className="pk-kit-price">
                              <span>
                                Price
                              </span>

                              <strong>
                                {formatMoney(
                                  kit.selling_price,
                                  kit.currency,
                                )}
                              </strong>
                            </div>
                          </div>

                          <div className="pk-feature-pills">
                            {Number(
                              kit.target_projects_count,
                            ) >
                              0 && (
                              <span>
                                {
                                  kit.target_projects_count
                                }{" "}
                                hands-on projects
                              </span>
                            )}

                            {kit.school_name && (
                              <span>
                                {
                                  kit.school_name
                                }
                              </span>
                            )}
                          </div>

                          <button
                            type="button"
                            className="pk-card-btn"
                            disabled={
                              stock <=
                              0
                            }
                            onClick={() =>
                              selectKit(
                                kit,
                                true,
                              )
                            }
                          >
                            {stock <=
                            0
                              ? "Out of Stock"
                              : active
                                ? "Continue to Order"
                                : "Select This Kit"}

                            <span aria-hidden="true">
                              →
                            </span>
                          </button>
                        </div>
                      </article>
                    );
                  },
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* =====================================================
          HOW IT WORKS
      ===================================================== */}

      <section
        id="how-it-works"
        className="pk-process-section"
      >
        <div className="pk-shell">
          <div className="pk-centered-heading">
            <span className="pk-section-kicker">
              HOW IT WORKS
            </span>

            <h2>
              Three simple steps.
            </h2>
          </div>

          <div className="pk-process-grid">
            {[
              [
                "01",
                "Choose school & grade",
                "Select the student's school and current grade.",
              ],

              [
                "02",
                "Select the kit",
                "Choose the Project Kit shown for that school and grade.",
              ],

              [
                "03",
                "Place the order",
                "Add contact, optional Student Coupon, delivery and payment details, then submit.",
              ],
            ].map(
              ([
                no,
                title,
                text,
              ]) => (
                <article
                  key={
                    no
                  }
                  className="pk-process-card"
                >
                  <span className="pk-step-number">
                    {no}
                  </span>

                  <h3>
                    {title}
                  </h3>

                  <p>
                    {text}
                  </p>
                </article>
              ),
            )}
          </div>
        </div>
      </section>

      {/* =====================================================
          ORDER
      ===================================================== */}

      <section
        ref={
          orderSectionRef
        }
        className="pk-order-section"
      >
        <div className="pk-shell pk-order-layout">
          <div className="pk-order-heading">
            <span className="pk-section-kicker">
              ORDER YOUR PROJECT KIT
            </span>

            <h2>
              Complete your order.
            </h2>

            <p>
              Add the student's details, apply a Student Coupon if available,
              choose delivery and payment, then review the total before placing
              the order.
            </p>
          </div>

          {orderSuccess ? (
            /*
             * ------------------------------------------------
             * SUCCESS SCREEN
             * ------------------------------------------------
             */

            <section
              className="pk-order-success"
              aria-live="polite"
            >
              <div
                className="pk-success-icon"
                aria-hidden="true"
              >
                ✓
              </div>

              <span className="pk-success-kicker">
                ORDER CONFIRMED
              </span>

              <h3>
                Order placed successfully
              </h3>

              <p>
                Keep this order number for reference. You can place another
                Project Kit order whenever you need additional kits.
              </p>

              <div className="pk-success-number">
                <span>
                  Order number
                </span>

                <strong>
                  {
                    orderSuccess.order_number
                  }
                </strong>
              </div>

              <dl className="pk-success-details">
                <div>
                  <dt>
                    Kit
                  </dt>

                  <dd>
                    {
                      orderSuccess.kit_title
                    }
                  </dd>
                </div>

                <div>
                  <dt>
                    Student
                  </dt>

                  <dd>
                    {
                      orderSuccess.student_name
                    }
                  </dd>
                </div>

                <div>
                  <dt>
                    Quantity
                  </dt>

                  <dd>
                    {
                      orderSuccess.quantity
                    }
                  </dd>
                </div>

                <div>
                  <dt>
                    Total
                  </dt>

                  <dd>
                    {formatMoney(
                      orderSuccess.total_amount,
                      orderSuccess.currency,
                    )}
                  </dd>
                </div>

                {Number(
                  orderSuccess.discount_amount ||
                    0,
                ) >
                  0 && (
                  <div>
                    <dt>
                      Coupon
                    </dt>

                    <dd>
                      {orderSuccess.promo_code ||
                        "Applied"}
                    </dd>
                  </div>
                )}

                {Number(
                  orderSuccess.discount_amount ||
                    0,
                ) >
                  0 && (
                  <div>
                    <dt>
                      Discount
                    </dt>

                    <dd>
                      −
                      {formatMoney(
                        orderSuccess.discount_amount,
                        orderSuccess.currency,
                      )}
                    </dd>
                  </div>
                )}

                <div>
                  <dt>
                    Payment status
                  </dt>

                  <dd className="pk-capitalize">
                    {
                      orderSuccess.payment_status
                    }
                  </dd>
                </div>

                <div>
                  <dt>
                    Delivery
                  </dt>

                  <dd className="pk-capitalize">
                    {
                      orderSuccess.delivery_type
                    }
                  </dd>
                </div>
              </dl>

              <button
                type="button"
                className="pk-submit-btn"
                onClick={
                  startAnotherOrder
                }
              >
                Start Another Order

                <span aria-hidden="true">
                  →
                </span>
              </button>
            </section>
          ) : !selectedKit ? (
            /*
             * ------------------------------------------------
             * NO KIT SELECTED
             * ------------------------------------------------
             */

            <div className="pk-order-empty">
              <div
                className="pk-order-empty-icon"
                aria-hidden="true"
              >
                ↑
              </div>

              <h3>
                Select a Project Kit first
              </h3>

              <p>
                Choose your school and grade above, then select the available
                Project Kit to continue.
              </p>

              <button
                type="button"
                className="pk-primary-btn"
                onClick={
                  scrollToCatalog
                }
              >
                Find My Kit

                <span aria-hidden="true">
                  →
                </span>
              </button>
            </div>
          ) : (
            <>
              <div className="pk-order-product">
                <img
                  src={
                    selectedKit?.offering_image_url ||
                    projectKitImage
                  }
                  alt={
                    getKitTitle(
                      selectedKit,
                    )
                  }
                />

                <div className="pk-order-product-copy">
                  <span>
                    Selected Kit
                  </span>

                  <strong>
                    {getKitTitle(
                      selectedKit,
                    )}
                  </strong>

                  <small>
                    {selectedKit?.target_grade_or_level ||
                      orderForm.grade}

                    {selectedSchool
                      ? ` • ${selectedSchool.school_name}`
                      : ""}
                  </small>
                </div>

                <div className="pk-order-product-price">
                  <span>
                    Price
                  </span>

                  <strong>
                    {formatMoney(
                      selectedKit.selling_price,
                      selectedKit.currency,
                    )}
                  </strong>
                </div>
              </div>

              <form
                className="pk-order-form"
                onSubmit={
                  handleSubmit
                }
                noValidate
              >
                <div className="pk-form-head">
                  <div>
                    <span>
                      ORDER DETAILS
                    </span>

                    <h3>
                      Student & delivery details
                    </h3>
                  </div>

                  <span className="pk-secure-pill">
                    MindSpark
                  </span>
                </div>

                {/* WHO IS ORDERING */}

                <div className="pk-field-group">
                  <label>
                    Who is placing the order?
                  </label>

                  <div className="pk-choice-grid three">
                    {ordererOptions.map(
                      (option) => (
                        <button
                          key={
                            option
                          }
                          type="button"
                          className={
                            orderForm.orderedBy ===
                            option
                              ? "active"
                              : ""
                          }
                          onClick={() =>
                            updateField(
                              "orderedBy",
                              option,
                            )
                          }
                        >
                          {
                            option
                          }
                        </button>
                      ),
                    )}
                  </div>
                </div>

                {/* STUDENT + QUANTITY */}

                <div className="pk-form-grid two">
                  <div className="pk-field-group">
                    <label htmlFor="studentName">
                      Student's full name
                    </label>

                    <input
                      id="studentName"
                      value={
                        orderForm.studentName
                      }
                      onChange={(
                        event,
                      ) =>
                        updateField(
                          "studentName",
                          event.target.value,
                        )
                      }
                      placeholder="Enter student's full name"
                    />
                  </div>

                  <div className="pk-field-group">
                    <label htmlFor="quantity">
                      Quantity
                    </label>

                    <div className="pk-quantity-control">
                      <button
                        type="button"
                        disabled={
                          quantity <=
                            1 ||
                          isSubmitting
                        }
                        onClick={() =>
                          updateField(
                            "quantity",
                            Math.max(
                              1,
                              quantity -
                                1,
                            ),
                          )
                        }
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>

                      <input
                        id="quantity"
                        type="number"
                        min="1"
                        max={
                          maxQuantity ||
                          1
                        }
                        disabled={
                          isSubmitting
                        }
                        value={
                          orderForm.quantity
                        }
                        onChange={(
                          event,
                        ) => {
                          const raw =
                            Number(
                              event
                                .target
                                .value ||
                                1,
                            );

                          const next =
                            Math.floor(
                              raw,
                            );

                          updateField(
                            "quantity",
                            Math.min(
                              maxQuantity ||
                                1,
                              Math.max(
                                1,
                                next,
                              ),
                            ),
                          );
                        }}
                      />

                      <button
                        type="button"
                        disabled={
                          quantity >=
                            maxQuantity ||
                          isSubmitting
                        }
                        onClick={() =>
                          updateField(
                            "quantity",
                            Math.min(
                              maxQuantity,
                              quantity +
                                1,
                            ),
                          )
                        }
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>

                    <small className="pk-stock-note">
                      {
                        selectedKitStock
                      }{" "}
                      currently in stock
                    </small>
                  </div>
                </div>

                {/* CONTACT DETAILS */}

                <div className="pk-form-grid two">
                  <div className="pk-field-group">
                    <label htmlFor="mobile">
                      Parent / Student mobile number
                    </label>

                    <input
                      id="mobile"
                      type="tel"
                      value={
                        orderForm.mobile
                      }
                      onChange={(
                        event,
                      ) =>
                        updateField(
                          "mobile",
                          event.target.value,
                        )
                      }
                      placeholder="+91"
                    />
                  </div>

                  <div className="pk-field-group">
                    <label htmlFor="email">
                      Email address (optional)
                    </label>

                    <input
                      id="email"
                      type="email"
                      value={
                        orderForm.email
                      }
                      onChange={(
                        event,
                      ) =>
                        updateField(
                          "email",
                          event.target.value,
                        )
                      }
                      placeholder="name@example.com"
                    />
                  </div>
                </div>

                {/* =================================================
                    STUDENT COUPON
                ================================================= */}

                <section
                  className="pk-coupon-panel"
                  aria-label="Student Coupon"
                >
                  <div className="pk-coupon-head">
                    <div>
                      <span>
                        STUDENT COUPON
                      </span>

                      <h4>
                        Have a Project Kit coupon?
                      </h4>

                      <p>
                        Enter the coupon code provided for the selected school.
                        Coupon eligibility and the discount are checked securely
                        before the order is placed.
                      </p>
                    </div>

                    <div className="pk-coupon-badge">
                      Optional
                    </div>
                  </div>

                  <div className="pk-coupon-fields">
                    <div className="pk-field-group">
                      <label htmlFor="promoCode">
                        Coupon code
                      </label>

                      <div className="pk-coupon-input-row">
                        <input
                          id="promoCode"
                          value={
                            orderForm.promoCode
                          }
                          onChange={(
                            event,
                          ) =>
                            updateField(
                              "promoCode",
                              event.target.value
                                .toUpperCase()
                                .replace(
                                  /\s+/g,
                                  "",
                                ),
                            )
                          }
                          placeholder="STUDENT10"
                          autoComplete="off"
                        />

                        {couponPreview ? (
                          <button
                            type="button"
                            className="pk-coupon-remove-btn"
                            onClick={
                              removeCoupon
                            }
                            disabled={
                              isSubmitting
                            }
                          >
                            Remove
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="pk-coupon-apply-btn"
                            onClick={
                              applyCoupon
                            }
                            disabled={
                              isApplyingCoupon ||
                              isSubmitting
                            }
                          >
                            {isApplyingCoupon
                              ? "Checking…"
                              : "Apply"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {couponStatus ===
                    "applied" &&
                    couponPreview && (
                      <div
                        className="pk-coupon-result success"
                        role="status"
                      >
                        <div
                          className="pk-coupon-result-icon"
                          aria-hidden="true"
                        >
                          ✓
                        </div>

                        <div>
                          <strong>
                            {
                              couponMessage
                            }
                          </strong>

                          <span>
                            You save{" "}
                            {formatMoney(
                              couponDiscount,
                              selectedKit.currency,
                            )}
                            . Final kit amount:{" "}
                            {formatMoney(
                              finalTotal,
                              selectedKit.currency,
                            )}
                            .
                          </span>
                        </div>
                      </div>
                    )}

                  {couponStatus ===
                    "error" &&
                    couponMessage && (
                      <div
                        className="pk-coupon-result error"
                        role="alert"
                      >
                        <div
                          className="pk-coupon-result-icon"
                          aria-hidden="true"
                        >
                          !
                        </div>

                        <div>
                          <strong>
                            Coupon not applied
                          </strong>

                          <span>
                            {
                              couponMessage
                            }
                          </span>
                        </div>
                      </div>
                    )}

                  <p className="pk-coupon-note">
                    Student Coupons are school-specific. The selected school,
                    Project Kit, quantity, coupon validity and usage limits are
                    checked securely before the discount is applied.
                  </p>
                </section>

                {/* DELIVERY */}

                <div className="pk-field-group pk-section-field">
                  <label>
                    Delivery preference
                  </label>

                  <div className="pk-choice-grid two">
                    {deliveryOptions.map(
                      (option) => (
                        <button
                          key={
                            option
                          }
                          type="button"
                          className={
                            orderForm.deliveryPreference ===
                            option
                              ? "active"
                              : ""
                          }
                          onClick={() =>
                            updateField(
                              "deliveryPreference",
                              option,
                            )
                          }
                        >
                          {
                            option
                          }
                        </button>
                      ),
                    )}
                  </div>
                </div>

                {/* HOME DELIVERY ADDRESS */}

                {orderForm.deliveryPreference ===
                  "Home Delivery" && (
                  <div className="pk-address-panel">
                    <div className="pk-field-group">
                      <label htmlFor="address">
                        Full delivery address
                      </label>

                      <textarea
                        id="address"
                        rows="3"
                        value={
                          orderForm.address
                        }
                        onChange={(
                          event,
                        ) =>
                          updateField(
                            "address",
                            event.target.value,
                          )
                        }
                        placeholder="House / flat, street, area, landmark"
                      />
                    </div>

                    <div className="pk-form-grid three">
                      <div className="pk-field-group">
                        <label htmlFor="city">
                          City
                        </label>

                        <input
                          id="city"
                          value={
                            orderForm.city
                          }
                          onChange={(
                            event,
                          ) =>
                            updateField(
                              "city",
                              event.target.value,
                            )
                          }
                        />
                      </div>

                      <div className="pk-field-group">
                        <label htmlFor="state">
                          State
                        </label>

                        <input
                          id="state"
                          value={
                            orderForm.state
                          }
                          onChange={(
                            event,
                          ) =>
                            updateField(
                              "state",
                              event.target.value,
                            )
                          }
                        />
                      </div>

                      <div className="pk-field-group">
                        <label htmlFor="pinCode">
                          PIN code
                        </label>

                        <input
                          id="pinCode"
                          inputMode="numeric"
                          maxLength="6"
                          value={
                            orderForm.pinCode
                          }
                          onChange={(
                            event,
                          ) =>
                            updateField(
                              "pinCode",
                              event.target.value,
                            )
                          }
                          placeholder="411001"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* PAYMENT */}

                <div className="pk-field-group pk-section-field">
                  <label>
                    Preferred payment method
                  </label>

                  <div className="pk-choice-grid payment">
                    {paymentOptions.map(
                      (option) => (
                        <button
                          key={
                            option
                          }
                          type="button"
                          className={
                            orderForm.paymentMethod ===
                            option
                              ? "active"
                              : ""
                          }
                          onClick={() =>
                            updateField(
                              "paymentMethod",
                              option,
                            )
                          }
                        >
                          {
                            option
                          }
                        </button>
                      ),
                    )}
                  </div>
                </div>

                {/* SUMMARY */}

                <div className="pk-order-summary">
                  <div>
                    <span>
                      Kit price ×{" "}
                      {quantity}
                    </span>

                    <strong>
                      {formatMoney(
                        subtotal,
                        selectedKit.currency,
                      )}
                    </strong>
                  </div>

                  {couponPreview &&
                    couponDiscount >
                      0 && (
                      <div className="pk-order-discount">
                        <span>
                          Coupon{" "}
                          {couponPreview.couponCode ||
                            orderForm.promoCode}
                        </span>

                        <strong>
                          −
                          {formatMoney(
                            couponDiscount,
                            selectedKit.currency,
                          )}
                        </strong>
                      </div>
                    )}

                  <div>
                    <span>
                      Delivery
                    </span>

                    <strong>
                      {
                        orderForm.deliveryPreference
                      }
                    </strong>
                  </div>

                  <div className="pk-order-total">
                    <span>
                      {couponPreview
                        ? "Final total"
                        : "Total"}
                    </span>

                    <strong>
                      {formatMoney(
                        finalTotal,
                        selectedKit.currency,
                      )}
                    </strong>
                  </div>
                </div>

                {/* ERROR */}

                {orderError && (
                  <div
                    className="pk-order-error"
                    role="alert"
                  >
                    <strong>
                      Order not placed
                    </strong>

                    <span>
                      {orderError}
                    </span>
                  </div>
                )}

                {/* SUBMIT */}

                <button
                  type="submit"
                  className="pk-submit-btn"
                  disabled={
                    isSubmitting ||
                    isApplyingCoupon
                  }
                >
                  {isSubmitting
                    ? "Placing Your Order…"
                    : "Place Project Kit Order"}

                  <span aria-hidden="true">
                    →
                  </span>
                </button>

                <p className="pk-form-footnote">
                  Please review the details above before placing the order.
                  Coupon eligibility and final pricing are rechecked securely
                  when the order is placed.
                </p>
              </form>
            </>
          )}
        </div>
      </section>

      {/* =====================================================
          FINAL CTA
      ===================================================== */}

      <section className="pk-final-cta">
        <div className="pk-shell pk-final-cta-inner">
          <div>
            <span>
              PROJECT KITS
            </span>

            <h2>
              Ready to find your kit?
            </h2>
          </div>

          <button
            type="button"
            onClick={
              scrollToCatalog
            }
          >
            Choose School & Grade

            <span aria-hidden="true">
              →
            </span>
          </button>
        </div>
      </section>
    </main>
  );
}